import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

// Tiny module-level store for the global "Uploading photo…" overlay.
// Multiple simultaneous uploads are reference-counted — the overlay
// stays on screen as long as any upload is in flight, and the
// `const u = showUploadingPhotoOverlay(); … u.dismiss();` pattern
// mirrors the shadcn toast API this replaced so call sites didn't
// need to learn a new shape.
let activeUploads = 0;
const listeners = new Set<(count: number) => void>();

function setActive(n: number) {
  activeUploads = n;
  listeners.forEach((l) => l(activeUploads));
}

export function showUploadingPhotoOverlay() {
  setActive(activeUploads + 1);
  let dismissed = false;
  return {
    dismiss: () => {
      if (dismissed) return;
      dismissed = true;
      setActive(Math.max(0, activeUploads - 1));
    },
  };
}

// Mounted once at the App root next to <Toaster />. Fixed center of
// the viewport, stays visible regardless of scroll, and intentionally
// BLOCKS interaction (dim backdrop + no pointer-events: none) so
// users can't accidentally Finish/Done a workout while the photo is
// still in flight. Photo uploads are short-lived and authoritative,
// so the blocking modal is the right call here.
export function UploadingOverlay() {
  const [count, setCount] = useState(activeUploads);
  useEffect(() => {
    listeners.add(setCount);
    return () => {
      listeners.delete(setCount);
    };
  }, []);

  if (count <= 0) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="alertdialog"
      aria-live="assertive"
      aria-modal="true"
      aria-label="Uploading photo"
    >
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-primary/40 bg-background px-10 py-8 shadow-2xl shadow-amber-500/20 max-w-[90vw]">
        <Loader2
          size={80}
          strokeWidth={2.5}
          className="text-primary animate-spin"
          aria-hidden="true"
        />
        <div className="text-center">
          <p className="text-xl font-semibold">Uploading photo…</p>
          <p className="text-sm text-muted-foreground mt-1.5">
            Keep the app open until this finishes.
          </p>
        </div>
      </div>
    </div>
  );
}
